import {ErrorMessage} from 'components/common/ErrorMessage'
import {useSendRecaptchaTokenMutation} from 'hooks/ventrata/useSendRecaptchaTokenMutation'
import {useUpdateOrderMutation} from 'hooks/ventrata/useUpdateOrderMutation'
import React, {useEffect} from 'react'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import {UpdateOrderRequest} from 'types/params'
import {singleCall} from 'utils/singleCall'
import {useConfig} from './ConfigProvider'
import {useConfirmingPath, useSiteUrl} from './EnvironmentProvider'
import {OrderProvider} from './OrderProvider'

type Props = {
  children: React.ReactNode
}

export const VentrataPaymentContext = React.createContext(null)

export const VentrataPaymentProvider = ({children}: Props) => {
  const {executeRecaptcha} = useGoogleReCaptcha()
  const {recaptchaEnterpriseSiteKey} = useConfig()
  const sendRecaptchaTokenMutation = useSendRecaptchaTokenMutation()
  const order = OrderProvider.useOrder()
  const setOrder = OrderProvider.useOrderSetter()
  const updateOrderMutation = useUpdateOrderMutation()
  const updateOrder = updateOrderMutation.mutate
  const confirmingPath = useConfirmingPath()
  const siteUrl = useSiteUrl()

  useEffect(() => {
    if (!executeRecaptcha) {
      return
    }

    singleCall('sendRecaptchaToken', async () => {
      const token = await executeRecaptcha('CHECKOUT')

      const updateParams: UpdateOrderRequest = {
        orderId: order.id,
        recaptchaEnterprise: {
          token,
          siteKey: recaptchaEnterpriseSiteKey,
        },
      }

      // If this is an Adyen order, and we have not yet set the returnUrl, add that to our update:
      if (order.cardPayment && order.cardPayment.gateway === 'adyen' && !order.returnUrl) {
        updateParams.returnUrl = `${siteUrl}${confirmingPath}`
      }

      updateOrder(updateParams, {onSuccess: setOrder})
    })
  }, [executeRecaptcha, recaptchaEnterpriseSiteKey, setOrder, updateOrder, order, confirmingPath, siteUrl])

  if (sendRecaptchaTokenMutation.error) {
    return <ErrorMessage error={sendRecaptchaTokenMutation.error} />
  }

  if (!order.recaptchaEnterpriseReceived) {
    return null
  }

  return <VentrataPaymentContext.Provider value={null}>{children}</VentrataPaymentContext.Provider>
}
